import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ToolsHelper from '@core/services/tools.helper';
import PosCheckoutKeyboard from '../pos-checkout-keyboard/pos-checkout-keyboard.vue';
import PosCheckoutPaymentsList from '@pos/components/lists/pos-checkout-payments-list/pos-checkout-payments-list.vue';
import { ConfirmationModalConfig } from '@shared/interfaces/confirmation-modal-config';
import ConfirmationModal from '@shared/components/modals/confirmation-modal/confirmation-modal.vue';
import moment from 'moment';
import { Observable } from 'rxjs';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import { CenterSetting } from '@core/interfaces/center-setting';
import WipayTerminalPaymentModal from '@accounting/components/payments/wipay-terminal/wipay-terminal-payment-modal/wipay-terminal-payment-modal.vue';
import { Terminal } from '@payment-terminals/interfaces/terminal';
import ModulesHelper from '@core/services/modules.helper';
import { BAlert } from 'bootstrap-vue';
import { PosSession } from '@pos/interfaces/pos-session';
import HtmlToPrinter from '@shared/components/html-to-printer/html-to-printer.vue';
import PrintTicket from '@accounting/components/details/print-ticket/print-ticket.vue';
import CashdroTerminalPaymentModal from '@accounting/components/payments/cashdro/cashdro-termina-payment-modal/cashdro-terminal-payment-modal.vue';
import { WipayPaymentMethod } from '@payment-terminals/interfaces/wipay';
import { Payment } from '@accounting/interfaces/payment';

@Component({
  name: 'bpc-pos-tools-checkout',
  components: {
    'font-awesome-icon': FontAwesomeIcon,
    'pos-checkout-keyboard': PosCheckoutKeyboard,
    'pos-checkout-payments-list': PosCheckoutPaymentsList,
    'bpc-confirmation-modal': ConfirmationModal,
    'bpc-wipay-terminal-payment-modal': WipayTerminalPaymentModal,
    'bpc-cashdro-terminal-payment-modal': CashdroTerminalPaymentModal,
    'b-alert': BAlert,
    'bpc-html-to-printer': HtmlToPrinter,
    'bpc-print-ticket': PrintTicket,
  },
})
export default class PosToolsCheckout extends Vue {
  $refs: {
    // resourceForm: InstanceType<typeof ResourceForm> & { saveEntity(): void };
    checkoutPaymentList: InstanceType<typeof PosCheckoutPaymentsList> & {
      getEntities(): void;
      payments: Payment[];
    };
    htmlToPrinter: InstanceType<typeof HtmlToPrinter> & {
      printContent(): void;
    };
  };
  public disableAcceptConfirmationModalButton: boolean = false;
  public savingPayment: boolean = false;
  public closingTicket: boolean = false;
  public saveEntitySubscription = null;
  public successText: string = 'global.generic.successful_action';
  public errorText: string = 'global.generic.error_form_action';

  public toPay: number = null;
  public paymentMethod = 'cash';

  public showWipayTerminalPaymentModal = false;
  public showCashdroTerminalPaymentModal = false;
  public wipayTerminalKey = 0;
  public cashdroTerminalKey = 0;
  public wipayPaymentMethod: WipayPaymentMethod = 'wipay_terminal';

  get terminals(): Terminal[] {
    return this.$store.getters['paymentTerminals/terminals'];
  }

  get wipayTerminals(): Terminal[] {
    const terminals = this.$store.getters['paymentTerminals/terminals'];
    let wipayTerminals = [];
    if (terminals) {
      wipayTerminals = terminals.filter(terminal => terminal.type === 'wipay');
    }
    return wipayTerminals;
  }
  get cashdroTerminals(): Terminal[] {
    const terminals = this.$store.getters['paymentTerminals/terminals'];
    let cashdroTerminals = [];
    if (terminals) {
      const posBoxIds = this.openPosSession?.pos_box_id;
      cashdroTerminals = terminals.filter(
        terminal => terminal.type === 'cashdro' && terminal.pos_box_ids.includes(posBoxIds),
      );
    }
    return cashdroTerminals;
  }

  get paymentConfirmationModalConfig(): ConfirmationModalConfig {
    const method = this.$t('global.payment.' + this.paymentMethod);
    return {
      id: 'pos-payment-creation-modal',
      title: this.$t('global.pos_creation_payment_modal_title', { method }),
      body: this.$t('global.pos_creation_payment_modal_body') as string,
      cancelText: this.$t('global.buttons.cancel') as string,
      acceptText: this.$t('global.buttons.save_payment', { method }) as string,
    };
  }
  get closeTicketConfirmationModalConfig(): ConfirmationModalConfig {
    return {
      id: 'pos-close-ticket-modal',
      title: this.$t('global.close_ticket_modal_title'),
      body: this.$t('global.close_ticket_modal_body') as string,
      cancelText: this.$t('global.buttons.cancel') as string,
      acceptText: this.$t('global.buttons.confirm') as string,
    };
  }

  get paymentMethodOptions(): any[] {
    const result = [];
    const payment_methods = this.openPosSession?.pos_box?.allowed_payment_methods;
    payment_methods.forEach(method => {
      switch (method) {
        case 'wipay_terminal': {
          const hasTerminalPaymentsModule = this.$modulesService.hasModuleActivated(
            ModulesHelper.PAYMENT_TERMINALS_MODULE_NAME,
          );
          if (hasTerminalPaymentsModule) {
            result.push({
              value: 'wipay_terminal',
              text: 'global.payment_method.wipay_terminal.label',
            });
          }
          break;
        }
        case 'cashdro': {
          const hasTerminalPaymentsModule = this.$modulesService.hasModuleActivated(
            ModulesHelper.PAYMENT_TERMINALS_MODULE_NAME,
          );
          if (hasTerminalPaymentsModule) {
            //TODO: check for what is used the text in the result array
            result.push({
              value: 'cashdro',
              text: 'global.payment_method.cashdro_terminal.label',
            });
          }
          break;
        }
        default: {
          result.push({
            value: method,
            text: 'global.payment.' + method,
          });
          break;
        }
      }
    });
    return result;
  }

  get actionAfterPayment(): string {
    const close = this.$store.getters['pos/currentPosBox'].close_after_payment;
    const print = this.$store.getters['pos/currentPosBox'].print_after_payment;

    if (print && close) {
      return 'print_and_close';
    } else if (close) {
      return 'close';
    } else if (print) {
      return 'print';
    }

    return 'nothing';
  }

  mounted() {
    this.listenToWebsocketEvents();
  }

  destroyed() {
    this.$bpcEcho.leaveTerminalPaymentChannel();
  }

  listenToWebsocketEvents() {
    this.$bpcEcho
      .getTerminalPaymentChannel()
      .listen('.BPC\\PaymentTerminals\\Events\\TerminalPaymentCancelled', data => {
        console.log(
          'TerminalPaymentCancelled listen event',
          data,
          'currentTicketId',
          this.currentTicket.id,
        );
        if (data.invoiceId === this.currentTicket.id) {
          console.log('checkoutPaymentList.getEntities()');
          this.$refs.checkoutPaymentList.getEntities();
        }
      })
      .listen('.BPC\\PaymentTerminals\\Events\\TerminalPaymentConfirmed', data => {
        console.log(
          'TerminalPaymentConfirmed listen event',
          data,
          'currentTicketId',
          this.currentTicket.id,
        );
        if (data.invoiceId === this.currentTicket.id) {
          console.log('checkoutPaymentList.getEntities()');
          this.$refs.checkoutPaymentList.getEntities();
        }
      })
      .listen('.BPC\\PaymentTerminals\\Events\\TerminalPaymentBusy', data => {
        console.log(
          'TerminalPaymentBusy listen event',
          data,
          'currentTicketId',
          this.currentTicket.id,
        );
        if (data.invoiceId === this.currentTicket.id) {
          console.log('checkoutPaymentList.getEntities()');
          this.$refs.checkoutPaymentList.getEntities();
        }
      });
  }

  onWipayTerminalPaymentCreated() {
    this.$refs.checkoutPaymentList.getEntities();
  }

  onCashdroTerminalPaymentCreated() {
    this.$refs.checkoutPaymentList.getEntities();
  }
  onCashdroTerminalPaymentCancelled() {
    this.$refs.checkoutPaymentList.getEntities();
  }

  async onCashdroTerminalPaymentFinalzed() {
    this.savingPayment = false;
    this.disableAcceptConfirmationModalButton = false;
    const currentTicket = await this.$store.dispatch('pos/reloadCurrentTicket');
    this.$refs.checkoutPaymentList.getEntities();
  }

  changeToPayValue($event) {
    this.toPay = $event;
  }

  public onCreatePayment(method = null) {
    if (method) {
      this.paymentMethod = method;
    }
    if (this.paymentMethod === 'wipay_terminal') {
      this.manageWipayTerminalPayment('wipay_terminal');
    } else if (this.paymentMethod === 'electronic_sport_voucher') {
      this.manageWipayTerminalPayment('electronic_sport_voucher');
    } else if (this.paymentMethod === 'cashdro') {
      this.manageCashdroTerminalPayment();
    } else {
      this.$bvModal.show('pos-payment-creation-modal');
      this.togglePosModalsOpened();
    }
  }

  createPayment() {
    this.disableAcceptConfirmationModalButton = true;
    const data = {
      invoice_id: this.currentTicket.id,
      status: 'paid',
      payment_date: moment().format('Y-MM-DD'),
      payment_time: moment().format('hh:mm:ss'),
      paid_amount: this.toPay,
      payment_method_type: this.paymentMethod,
      contact_id: this.currentTicket.contact_id,
      origin: 'pos',
      payment_channel: 'other',
    };
    if (this.amountToReturn) {
      data['amount_to_return'] = -this.amountToReturn;
    }
    this.savePayment(data);
    this.togglePosModalsOpened();
  }

  public savePayment(data: AnyEntity): void {
    this.savingPayment = true;

    const saveEntityPath = `${this.$bpcApiRouteHelper.getEntityPath('payment', this.lang)}`;
    const observable: Observable<BPCGenericApiResponse> = this.$bpcApi.post(saveEntityPath, data);

    if (this.saveEntitySubscription) {
      this.saveEntitySubscription.unsubscribe();
    }
    this.saveEntitySubscription = observable.subscribe(
      (res: BPCGenericApiResponse) => {
        this.$bpcMessage.showSuccessMessage({ text: this.successText });
        this.onSuccessPaymentCreation();
      },
      error => {
        this.$bpcMessage.showErrorMessage({ text: this.errorText });
        this.savingPayment = false;
        this.disableAcceptConfirmationModalButton = false;
      },
    );
  }

  async manageWipayTerminalPayment(wipayPaymentMethod: WipayPaymentMethod = 'wipay_terminal') {
    this.wipayPaymentMethod = wipayPaymentMethod;
    if (!this.terminals.length) {
      this.$bpcMessage.showErrorMessage({
        text: 'payment_terminals.terminals.there_is_no_terminal_configured',
      });
    } else if (!this.currentTicket) {
      this.$bpcMessage.showErrorMessage({
        text: 'payment_terminals.errors.there_is_no_current_ticket',
      });
    } else {
      this.showWipayTerminalPaymentModal = true;
    }
  }

  async manageCashdroTerminalPayment() {
    if (!this.terminals.length) {
      this.$bpcMessage.showErrorMessage({
        text: 'payment_terminals.terminals.there_is_no_terminal_configured',
      });
    } else if (!this.currentTicket) {
      this.$bpcMessage.showErrorMessage({
        text: 'payment_terminals.errors.there_is_no_current_ticket',
      });
    } else {
      this.showCashdroTerminalPaymentModal = true;
    }
  }

  public onCloseWipayTerminalPaymentModal() {
    this.wipayTerminalKey++;
    this.showWipayTerminalPaymentModal = false;
    this.savingPayment = false;
  }
  public onCloseCashdroTerminalPaymentModal() {
    this.cashdroTerminalKey++;
    this.showCashdroTerminalPaymentModal = false;
    this.savingPayment = false;
  }

  public async onSuccessWipayTerminalPayment($event) {
    // NOTE: uncomment this to close the modal directly when the payment is accepted
    // for now we want to keep it opened to show to wipay ticket
    if (
      this.actionAfterPayment === 'print' ||
      this.actionAfterPayment === 'close' ||
      this.actionAfterPayment === 'print_and_close'
    ) {
      this.wipayTerminalKey++;
      this.showWipayTerminalPaymentModal = false;
    }

    this.$refs.checkoutPaymentList.getEntities();
    this.savingPayment = false;
    this.disableAcceptConfirmationModalButton = false;
    const currentTicket = await this.$store.dispatch('pos/reloadCurrentTicket');

    if (currentTicket.status === 'paid') {
      if (this.actionAfterPayment === 'print') {
        this.onPrint();
      } else if (this.actionAfterPayment === 'close') {
        this.closeTicket();
      } else if (this.actionAfterPayment === 'print_and_close') {
        this.onPrint();
        this.closeTicket();
      }
    }

    // setProp(this.$refs.paymentStdForm.formModel, 'payment_method_result', $event);
    // setProp(this.$refs.paymentStdForm.formModel, 'invoice_id', this.entity.id);
    // this.$refs.paymentStdForm.saveEntity();
  }

  public async onSuccessCashdroTerminalPayment($event) {
    if (
      this.actionAfterPayment === 'print' ||
      this.actionAfterPayment === 'close' ||
      this.actionAfterPayment === 'print_and_close'
    ) {
      this.cashdroTerminalKey++;
      this.showCashdroTerminalPaymentModal = false;
    }

    this.$refs.checkoutPaymentList.getEntities();
    this.savingPayment = false;
    this.disableAcceptConfirmationModalButton = false;
    const currentTicket = await this.$store.dispatch('pos/reloadCurrentTicket');

    if (currentTicket.status === 'paid') {
      if (this.actionAfterPayment === 'print') {
        this.onPrint();
      } else if (this.actionAfterPayment === 'close') {
        this.closeTicket();
      } else if (this.actionAfterPayment === 'print_and_close') {
        this.onPrint();
        this.closeTicket();
      }
    }
  }

  markingAsPaid: boolean = false;
  async onMarkAsPaid() {
    this.markingAsPaid = true;
    const saveEntityPath = `${this.$bpcApiRouteHelper.getEntityPath(
      'invoice',
      this.lang,
    )}/single-mark-as-paid/${this.currentTicketId}`;
    this.$bpcApi.post(saveEntityPath, {}).subscribe(
      async (res: BPCGenericApiResponse) => {
        const currentTicket = await this.$store.dispatch('pos/reloadCurrentTicket');
        this.markingAsPaid = false;
        if (currentTicket.status === 'paid') {
          if (this.$store.getters['pos/currentPosBox'].action_after_payment == 'print') {
            this.onPrint();
          } else if (this.$store.getters['pos/currentPosBox'].action_after_payment == 'close') {
            this.onClose();
          }
        }
      },
      error => {
        this.markingAsPaid = false;
        this.$bpcMessage.showErrorMessage({ text: this.errorText });
      },
    );
  }

  public async onSuccessPaymentCreation() {
    this.$refs.checkoutPaymentList.getEntities();
    this.$bvModal.hide('pos-payment-creation-modal');
    this.savingPayment = false;
    this.disableAcceptConfirmationModalButton = false;
    const currentTicket = await this.$store.dispatch('pos/reloadCurrentTicket');
    console.log(currentTicket);
    if (currentTicket.status === 'paid') {
      if (this.actionAfterPayment === 'print') {
        this.onPrint();
      } else if (this.actionAfterPayment === 'close') {
        this.closeTicket();
      } else if (this.actionAfterPayment === 'print_and_close') {
        this.onPrint();
        this.closeTicket();
      }
    }
  }

  public onPrint() {
    this.$refs.htmlToPrinter.printContent();
  }

  public onStartPrinting() {
    this.$store.commit('pos/changeIsPrinting', true);
  }

  public onStopPrinting() {
    this.$store.commit('pos/changeIsPrinting', false);
  }

  public async onClose() {
    const currentTicket = await this.$store.dispatch('pos/reloadCurrentTicket');
    console.log(currentTicket);
    if (currentTicket.status === 'paid') {
      this.closeTicket();
    } else {
      this.$bvModal.show('pos-close-ticket-modal');
    }
  }

  public onReopen() {
    this.$store.dispatch('pos/reopenCurrentTicket');
  }

  public closeTicket() {
    this.closingTicket = true;
    this.disableAcceptConfirmationModalButton = true;

    const saveEntityPath = `${this.$bpcApiRouteHelper.getEntityPath('invoice', this.lang)}/${
      this.currentTicket.id
    }/close-ticket`;
    const observable: Observable<BPCGenericApiResponse> = this.$bpcApi.put(saveEntityPath, {});

    if (this.saveEntitySubscription) {
      this.saveEntitySubscription.unsubscribe();
    }
    this.saveEntitySubscription = observable.subscribe(
      (res: BPCGenericApiResponse) => {
        this.$bpcMessage.showSuccessMessage({ text: this.successText });
        this.onSuccessCloseTicket();
      },
      error => {
        this.$bpcMessage.showErrorMessage({ text: this.errorText });
        this.closingTicket = false;
        this.disableAcceptConfirmationModalButton = false;
      },
    );
  }
  public onSuccessCloseTicket() {
    this.$store.commit('pos/clearCurrentTicket');
    this.$store.dispatch('pos/getOpenTickets');
    this.$bvModal.hide('pos-close-ticket-modal');
    this.closingTicket = false;
    this.disableAcceptConfirmationModalButton = false;
    // Use this event to avoid creating a function with the same logic because we only need to refresh the table.
    this.$root.$emit('refresh-table-after-add-orders-to-pos');
  }

  get amountToPayFormatted(): string {
    if (this.toPay) {
      return ToolsHelper.formatPrice(this.toPay);
    }
    return ToolsHelper.formatPrice(0);
  }
  get differenceAmount(): number {
    let difference = 0;
    if (this.currentTicket) {
      difference = Math.max(this.currentTicket.balance - this.toPay, 0);
    }
    return difference;
  }
  get differenceAmountFormatted(): string {
    return ToolsHelper.formatPrice(this.differenceAmount);
  }
  get amountToReturnFormatted(): string {
    return ToolsHelper.formatPrice(this.amountToReturn);
  }
  get amountToReturn(): number {
    let toReturn = 0;
    if (this.currentTicket) {
      toReturn = Math.max(this.toPay - this.currentTicket.balance, 0);
    }
    return toReturn;
  }

  get orderItems(): any[] {
    return this.$store.getters['pos/orderItems'];
  }

  get posUserName() {
    const posSession = this.$store.getters['pos/openPosSession'];
    return posSession?.profile.user.name;
  }
  get isPrintingTicket() {
    return this.$store.getters['pos/isPrinting'] || false;
  }

  get currentTicket() {
    return this.$store.getters['pos/currentTicket'];
  }

  get currentTicketCanBeMarkedAsPaid() {
    return (
      this.currentTicket?.status !== 'paid' &&
      this.currentTicket?.total_amount === 0 &&
      this.$store.getters['pos/orderItems'].length > 1 &&
      this.$store.getters['pos/orderItems'].find(item => {
        return item['_rowVariant'] === 'order' && item['invoice_id'] === this.currentTicket.id;
      })
    );
  }

  @Watch('currentTicket')
  onChangeCurrentTicket() {
    if (this.currentTicket) {
      if (this.currentTicket.balance) {
        this.toPay = this.currentTicket.balance;
      } else {
        this.toPay = 0;
      }
    }
  }
  get currentTicketId() {
    return this.$store.getters['pos/currentTicketId'];
  }
  @Watch('currentTicketId')
  onChangeCurrentTicketId() {
    if (this.currentTicket) {
      setTimeout(() => {
        this.$refs.checkoutPaymentList.getEntities();
      });
    }
  }

  get lang(): string {
    return this.$store.getters['auth/userLang'] || this.$i18n.locale;
  }

  get centerSetting(): CenterSetting {
    return this.$store.getters['centerSettings/centerSettings'];
  }

  get openPosSession(): PosSession {
    return this.$store.getters['pos/openPosSession'];
  }

  togglePosModalsOpened() {
    this.$store.commit('pos/toggleModalsOpened');
  }

  payments = [];
  onLoadPayments($event) {
    this.payments = $event;
    this.payments.forEach(
      payment => (payment.payment_method_type = payment.payment_method_type_value),
    );
  }
}
